import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { fromZonedTime } from 'date-fns-tz';

interface PauseTimeByMakeModelProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface MakePauseStats {
  make: string;
  totalPauseMinutes: number;
  avgPausePerTicket: number;
  ticketCount: number;
}

interface ModelPauseStats {
  model: string;
  totalPauseMinutes: number;
  avgPausePerTicket: number;
  ticketCount: number;
}

export function PauseTimeByMakeModel({ shopId, startDate, endDate, shopTimezone }: PauseTimeByMakeModelProps) {
  const [loading, setLoading] = useState(true);
  const [makeStats, setMakeStats] = useState<MakePauseStats[]>([]);
  const [expandedMake, setExpandedMake] = useState<string | null>(null);
  const [modelDrilldown, setModelDrilldown] = useState<ModelPauseStats[]>([]);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);

  useEffect(() => {
    loadMakePauseStats();
    // KPI-R70-FIX (MED-1): shopTimezone dep — fromZonedTime inside would
    // otherwise use a stale TZ for UTC bounds after a shop switch.
  }, [shopId, startDate, endDate, shopTimezone]);

  async function loadMakePauseStats() {
    setLoading(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get pause sessions - only completed ones (with duration)
      // This naturally excludes active pauses on parking lot tickets
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('id, ticket_id, duration_minutes')
        .eq('shop_id', shopId)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null);

      if (!pauseSessions?.length) {
        setMakeStats([]);
        setLoading(false);
        return;
      }

      // Get ticket vehicle info
      const ticketIds = [...new Set(pauseSessions.map(s => s.ticket_id))];
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id, vehicle_id')
        .in('id', ticketIds);

      const vehicleIds = [...new Set(tickets?.map(t => t.vehicle_id).filter(Boolean) || [])];
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id, make')
        .in('id', vehicleIds);

      const vehicleMakeMap = new Map(vehicles?.map(v => [v.id, v.make]) || []);
      const ticketMakeMap = new Map<string, string>();
      tickets?.forEach(t => {
        const make = vehicleMakeMap.get(t.vehicle_id);
        if (make) ticketMakeMap.set(t.id, make);
      });

      // Aggregate by make
      const makeMap = new Map<string, { pause: number; tickets: Set<string> }>();
      
      pauseSessions.forEach(s => {
        const make = ticketMakeMap.get(s.ticket_id) || 'Unknown';
        const existing = makeMap.get(make) || { pause: 0, tickets: new Set() };
        existing.pause += s.duration_minutes || 0;
        existing.tickets.add(s.ticket_id);
        makeMap.set(make, existing);
      });

      const stats: MakePauseStats[] = Array.from(makeMap.entries())
        .map(([make, data]) => ({
          make,
          totalPauseMinutes: data.pause,
          avgPausePerTicket: data.tickets.size > 0 ? Math.round(data.pause / data.tickets.size) : 0,
          ticketCount: data.tickets.size,
        }))
        .sort((a, b) => b.totalPauseMinutes - a.totalPauseMinutes);

      setMakeStats(stats);
    } catch (error) {
      console.error('Error loading make pause stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadModelDrilldown(make: string) {
    setLoadingDrilldown(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get vehicles for this make
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id, model')
        .eq('make', make);

      if (!vehicles?.length) {
        setModelDrilldown([]);
        return;
      }

      const vehicleIds = vehicles.map(v => v.id);
      const vehicleModelMap = new Map(vehicles.map(v => [v.id, v.model]));

      // Get tickets for these vehicles
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id, vehicle_id')
        .eq('shop_id', shopId)
        .in('vehicle_id', vehicleIds);

      const ticketIds = tickets?.map(t => t.id) || [];
      const ticketModelMap = new Map<string, string>();
      tickets?.forEach(t => {
        const model = vehicleModelMap.get(t.vehicle_id);
        if (model) ticketModelMap.set(t.id, model);
      });

      // Get pause sessions
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('ticket_id, duration_minutes')
        .eq('shop_id', shopId)
        .in('ticket_id', ticketIds)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null);

      // Aggregate by model
      const modelMap = new Map<string, { pause: number; tickets: Set<string> }>();
      
      pauseSessions?.forEach(s => {
        const model = ticketModelMap.get(s.ticket_id) || 'Unknown';
        const existing = modelMap.get(model) || { pause: 0, tickets: new Set() };
        existing.pause += s.duration_minutes || 0;
        existing.tickets.add(s.ticket_id);
        modelMap.set(model, existing);
      });

      const stats: ModelPauseStats[] = Array.from(modelMap.entries())
        .map(([model, data]) => ({
          model,
          totalPauseMinutes: data.pause,
          avgPausePerTicket: data.tickets.size > 0 ? Math.round(data.pause / data.tickets.size) : 0,
          ticketCount: data.tickets.size,
        }))
        .sort((a, b) => b.totalPauseMinutes - a.totalPauseMinutes);

      setModelDrilldown(stats);
    } catch (error) {
      console.error('Error loading model drilldown:', error);
    } finally {
      setLoadingDrilldown(false);
    }
  }

  function handleMakeClick(make: string) {
    if (expandedMake === make) {
      setExpandedMake(null);
      setModelDrilldown([]);
    } else {
      setExpandedMake(make);
      loadModelDrilldown(make);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pause Time by Make & Model</CardTitle>
        <CardDescription>
          Click a make to see model breakdown
        </CardDescription>
      </CardHeader>
      <CardContent>
        {makeStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No pause data in this date range</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Make</TableHead>
                <TableHead className="text-right">Total Pause</TableHead>
                <TableHead className="text-right">Avg/Ticket</TableHead>
                <TableHead className="text-right">Tickets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {makeStats.map((make) => (
                <>
                  <TableRow 
                    key={make.make}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleMakeClick(make.make)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expandedMake === make.make ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {make.make}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(make.totalPauseMinutes)}</TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(make.avgPausePerTicket)}</TableCell>
                    <TableCell className="text-right">{make.ticketCount}</TableCell>
                  </TableRow>
                  {expandedMake === make.make && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-muted/30 p-4">
                        {loadingDrilldown ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : modelDrilldown.length === 0 ? (
                          <p className="text-muted-foreground text-sm text-center">No model data</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Model</TableHead>
                                <TableHead className="text-right">Total Pause</TableHead>
                                <TableHead className="text-right">Avg/Ticket</TableHead>
                                <TableHead className="text-right">Tickets</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {modelDrilldown.map((model) => (
                                <TableRow key={model.model}>
                                  <TableCell>{model.model}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(model.totalPauseMinutes)}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(model.avgPausePerTicket)}</TableCell>
                                  <TableCell className="text-right">{model.ticketCount}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
